<template>
  <div>
    <page-header title="作品运营数据" description="查看各平台作品的公开指标和更新情况">
      <el-button type="primary" @click="create">录入作品</el-button>
      <el-button @click="importing=true">导入 Excel</el-button>
      <el-button @click="runs=true">运行记录</el-button>
    </page-header>
    <el-alert class="mb" type="info" :closable="false" show-icon title="更新说明" description="B站和抖音仅可在筛选日期为今天时手动更新；小红书与视频号仅展示各自授权采集流程已同步的数据。"/>
    <el-form inline>
      <el-select v-model="platform" clearable placeholder="全部平台">
        <el-option v-for="x in platforms" :key="x.value" :label="x.label" :value="x.value"/>
      </el-select>
      <el-date-picker v-model="updatedDate" type="date" value-format="yyyy-MM-dd" placeholder="更新核对日期"/>
      <el-button type="primary" plain @click="search">查询</el-button>
      <el-button :disabled="!canRefreshToday" :title="refreshHint" @click="refreshPlatform('bilibili')">更新 B站</el-button>
      <el-button :disabled="!canRefreshToday" :title="refreshHint" @click="refreshPlatform('douyin')">更新抖音</el-button>
    </el-form>
    <el-card v-loading="loading">
      <el-table :data="items" empty-text="当前平台暂无作品">
        <el-table-column type="index" :index="index" label="序号" width="65" align="center"/>
        <el-table-column label="作品标题" min-width="260">
          <template slot-scope="s">
            <a v-if="isURL(s.row.source_url)" :href="s.row.source_url" target="_blank">{{s.row.title}}</a>
            <span v-else>{{s.row.title}}</span>
            <div class="muted">{{s.row.source_id}}</div>
          </template>
        </el-table-column>
        <el-table-column label="平台" width="100"><template slot-scope="s"><platform-tag :value="s.row.platform"/></template></el-table-column>
        <el-table-column label="发布日期" width="120"><template slot-scope="s">{{dateOnly(s.row.published_at)}}</template></el-table-column>
        <el-table-column label="更新日期" width="165"><template slot-scope="s">{{formatDate(s.row.last_captured_at)||'暂未更新'}}</template></el-table-column>
        <el-table-column label="最新公开指标" min-width="180"><template slot-scope="s"><metric-list :metrics="s.row.latest_metrics"/></template></el-table-column>
        <el-table-column label="状态" width="165">
          <template slot-scope="s">
            <el-tag v-if="updatedDate&&updatedOnDate(s.row.last_captured_at)" type="success">已更新</el-tag>
            <span v-else-if="updatedDate" class="failed">未在 {{updatedDate}} 更新</span>
            <el-tag v-else-if="s.row.last_status==='failed'" type="danger">更新失败</el-tag>
            <el-tag v-else-if="s.row.last_status==='success'" type="success">已更新</el-tag>
            <el-tag v-else type="info">等待更新</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="180">
          <template slot-scope="s">
            <el-button type="text" @click="openMetrics(s.row)">趋势</el-button>
            <el-button type="text" @click="edit(s.row)">编辑资料</el-button>
            <el-button v-if="supportsRefresh(s.row)" type="text" :disabled="!canRefreshToday" :title="refreshHint" @click="refresh(s.row)">更新</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination">
        <el-pagination background layout="total, prev, pager, next, jumper" :current-page="page" :page-size="pageSize" :total="total" @current-change="changePage"/>
      </div>
    </el-card>
    <creator-form-dialog :visible.sync="formVisible" :editing="editing" @success="search"/>
    <creator-run-dialogs :visible.sync="runs"/>
    <creator-metrics-drawer :visible.sync="metricsVisible" :item="metricsItem"/>
    <el-dialog title="导入作品链接 Excel" :visible.sync="importing" width="460px">
      <el-upload :auto-upload="false" :limit="1" accept=".xlsx" :on-change="f=>file=f.raw"><el-button>选择 Excel</el-button></el-upload>
      <span slot="footer"><el-button @click="importing=false">取消</el-button><el-button type="primary" @click="upload">导入</el-button></span>
    </el-dialog>
  </div>
</template>

<script>
import { yuqingApi } from '@/api/yuqing'
import PageHeader from '../components/PageHeader.vue'
import PlatformTag from '../components/PlatformTag.vue'
import MetricList from '../components/MetricList.vue'
import CreatorFormDialog from './CreatorFormDialog.vue'
import CreatorRunDialogs from './CreatorRunDialogs.vue'
import CreatorMetricsDrawer from './CreatorMetricsDrawer.vue'

export default {
  components: { PageHeader, PlatformTag, MetricList, CreatorFormDialog, CreatorRunDialogs, CreatorMetricsDrawer },
  data () {
    return {
      items: [], platform: '', updatedDate: '', loading: false, formVisible: false, editing: null, runs: false, importing: false, file: null, metricsVisible: false, metricsItem: null,
      page: 1, pageSize: 20, total: 0,
      platforms: [{ value: 'bilibili', label: 'B站' }, { value: 'douyin', label: '抖音' }, { value: 'xiaohongshu', label: '小红书' }, { value: 'wechat_channels', label: '视频号' }]
    }
  },
  computed: {
    canRefreshToday () { return this.updatedDate === this.today() },
    refreshHint () { return this.canRefreshToday ? '' : '仅可更新当天数据，请选择今天' }
  },
  mounted () { this.updatedDate = this.today(); this.load() },
  methods: {
    today () { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` },
    async load () {
      this.loading = true
      try {
        const params = [`page=${this.page}`, `page_size=${this.pageSize}`]
        if (this.platform) params.push(`platform=${encodeURIComponent(this.platform)}`)
        const result = await yuqingApi.get(`creator-content?${params.join('&')}`)
        this.items = result.items || []
        this.total = result.total || 0
      } catch (e) { this.$message.error(e.message) } finally { this.loading = false }
    },
    search () { this.page = 1; this.load() },
    changePage (page) { this.page = page; this.load() },
    index (index) { return (this.page - 1) * this.pageSize + index + 1 },
    create () { this.editing = null; this.formVisible = true },
    edit (row) { this.editing = row; this.formVisible = true },
    openMetrics (row) { this.metricsItem = row; this.metricsVisible = true },
    updatedOnDate (value) { return !!(value && this.updatedDate && String(value).slice(0, 10) === this.updatedDate) },
    supportsRefresh (row) { return row.platform === 'bilibili' || row.platform === 'douyin' },
    dateOnly (v) { return v && !String(v).startsWith('0001-01-01') ? String(v).slice(0, 10) : '-' },
    formatDate (v) { return v ? String(v).replace('T', ' ').slice(0, 16) : '' },
    isURL (v) { try { const u = new URL(v); return u.protocol === 'http:' || u.protocol === 'https:' } catch (e) { return false } },
    async refresh (row) { try { await yuqingApi.post(`creator-content/${row.id}/refresh`, {}); this.$message.success('作品指标已更新'); this.load() } catch (e) { this.$message.error(e.message) } },
    async refreshPlatform (platform) { try { await yuqingApi.post('creator-content/refresh', { platform }); this.$message.success('作品更新已启动'); this.load() } catch (e) { this.$message.error(e.message) } },
    async upload () {
      if (!this.file) return this.$message.warning('请选择 Excel')
      try { const f = new FormData(); f.append('file', this.file); await yuqingApi.upload('creator-content/import', f); this.$message.success('作品已导入'); this.importing = false; this.file = null; this.search() } catch (e) { this.$message.error(e.message) }
    }
  }
}
</script>

<style scoped>
.mb { margin-bottom: 16px }
.muted { color: #909399; font-size: 12px; margin-top: 4px }
.failed { color: #f56c6c; font-size: 12px }
.pagination { display: flex; justify-content: flex-end; margin-top: 16px }
</style>
